import type { QueryClient } from "@tanstack/react-query";

/**
 * Invalidate the interconnected member / group / employee query graph.
 *
 * Company membership, group rosters, the agency add-user picker, and employee
 * records are one coupled graph: a single member / role / group mutation can
 * touch all of them. Historically each mutation hand-picked its own invalidation
 * set and they drifted — some missed the group list, some the members list, and
 * the group hooks even targeted a second, unused `["companies"]` key factory
 * (so their cross-domain invalidations were silent no-ops). Route every such
 * mutation through here so correctness is the default rather than something each
 * author must re-derive (and get wrong). These are low-traffic admin screens, so
 * coarse root-level invalidation is cheap.
 *
 * Kept dependency-free (literal roots, no key-factory imports) so it can be
 * called from any hook module without risking an import cycle.
 *
 * NOTE: two company-key factories currently coexist — `["company"]` (the members
 * list, add-user picker, current company) and `["companies"]` (used elsewhere).
 * Both roots are invalidated here until they are merged; that consolidation is a
 * separate follow-up.
 */
const MEMBER_GRAPH_ROOTS = [
  ["groups"],
  ["company"],
  ["companies"],
  ["employees"],
] as const;

export function invalidateMemberGraph(queryClient: QueryClient): void {
  for (const queryKey of MEMBER_GRAPH_ROOTS) {
    queryClient.invalidateQueries({ queryKey });
  }
}
